import { inventoryCatalog, inventoryDefinition } from '../../inventory/definition.js';
import { inventoryStepOver, inventoryStepOverSizeId } from '../../inventory/step-over.js';
import { planLatchedObjectMove } from '../../movement/compound-drag.js';
import { latchedCreatures } from '../../movement/latch-link.js';
import { brokenLatchRemovals } from '../../actions/latch.js';
// check_push_and_pull_after_hit.php — the defence gates are Lane A's; the geometry is MV4's twin (movement/forced-movement.ts).
// The dry run in ability-use.ts executes these state gates before the first real draw.
import { meshesOf } from '../../../../../ft3d/engine/mesh-presentation.js';
import { sizeWidthFt } from '../../../encounter-map/ft3d/perception.js';
import { inventoryField, posCoord, sizeIdOf } from '../../../shared/array-types.js';
import { physicalCreatureBody } from '../../../shared/physical-creature-body.js';
import { type JanusCtx, type Row, HandBack, activeStatuses, ec, ecId, ecLean, handBack, inventoryId, itemField, record, scalar, upper, definitionNotHeld } from '../context.js';
import { targetGateWarning } from '../is-target-valid-for-effect.js';
import { janus, type JanusInput } from '../janus.js';
import { applyRawDamageToCreature } from '../rolls/damage-type-roll.js';
// MV4 (owner-authorized cross-lane edit, 2026-09-28): the displacement geometry is MV4's twin.
import { resolveForcedMovement, forcedMovementVector, type ForcedMoveReaders } from '../../movement/forced-movement.js';
import { predictFall, SPEED, type FallVerdict } from '../../movement/fall-check.js';
import { placedAoeSelectionSpan, type PlacedAoeReaders } from '../aoe/expand-placed-aoe-targets.js';
import { FALLING_DAMAGE_ID, resolveFallDamage } from './fall-damage.js';
import { applyRawDamageToInventory } from '../../inventory/ability-damage.js';
import { heldMovementPosition, inventoryTemplateValue, isTransitionNavigationProp } from '../../movement/inventory-support.js';
import { boundCurtainIds, boundDoorIds, inventoryOccupancyIncluded, inventoryStandingZone, SIZE_WIDTHS_FT } from '../../inventory/lift-occupancy.js';
import { encounterDifficultFootprints } from '../../movement/difficult-terrain.js';
import { inventoryFootprint, inventoryHeightFt, inventorySolid, normalizeTransform, physicalMeshBoundsFt } from '../../inventory/floor-move.js';
import { inventorySceneryShell, inventoryAuthoredSizeId, inventoryEncounterLink, inventoryEquipmentSlot, inventoryFixtureFlags, inventoryPropName } from '../../inventory/solid-obstacle.js';
import { dedicatedPropSpace, inventoryMeshDrawn, selectedPropMeshSpaceOrTier } from '../../inventory/prop-mesh-space.js';
import { readInventoryField, readItemUuidSlot } from '../../compile/inventory-raw-readers.js';
import { normalizeInventoryGeneration } from '../../compile/inventory-generation.js';
import { pageSceneShape } from '../../geometry/perception-context.js';
import { checkOpposingTeam } from '../check-opposing-team.js';
import { squeezeStateAt } from '../../movement/plain-walk-scope.js';
import { resolveMovementSqueeze20 } from '../../statuses/application-inputs-20.js';
import { encounterField } from '../../../shared/array-types.js';

function statusDefinition(ctx: JanusCtx, status: Row): Row | null {
  return record(status.status_arr) ?? ctx.read.definition(upper(status.status_item_id));
}
function checkDefenseCarriers(ctx: JanusCtx, target: Row): void {
  const check = (entry: unknown): void => {
    const wrapper = record(entry);
    const id = upper(itemField(wrapper, 'item_id') ?? entry);
    const source = record(wrapper?.item_arr) ?? (wrapper && Object.keys(wrapper).length > 1 ? wrapper : null)
      ?? ctx.read.definition(id);
    if (!source) handBack('check_push_and_pull_after_hit:defense-carrier-not-held');
    if (ctx.read.has(source, 'immune_to_forced_movement')
        || ctx.read.has(source, 'immune_to_forced_movement_from_size_category_number_below'))
      handBack('check_push_and_pull_after_hit:carrier-defense');
  };
  for (const section of ['creature_type_arr', 'creature_sub_type_arr', 'lineage_arr', 'class_arr', 'subclass_arr', 'background_arr']) {
    if (Array.isArray(target[section])) for (const entry of target[section]) check(entry);
  }
  for (const bucket of ['passive_ability', 'full_ability', 'ability', 'quick_ability', 'attack_ability',
    'free_ability', 'reaction_ability', 'opportunity_attack_ability']) {
    for (const entry of ctx.read.entries(target, bucket)) check(entry);
  }
  // A plain held weapon is not an unknown defense. Inspect its resolved carrier tree; unheld enchantments keep their existing gate.
  for (const raw of ctx.read.entries(target, 'inventory')) {
    const row = record(raw);
    if (!row) handBack('check_push_and_pull_after_hit:defense-carrier-not-held');
    const equipped = ctx.read.inventoryField?.(row, 'equipped') ?? record(row.inventory)?.equipped;
    if (!/^[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(String(scalar(equipped) ?? ''))) continue;
    const field = ctx.read.inventoryField ?? inventoryField;
    const enchantmentId = field(row, 'enchantment_template_id');
    if (scalar(enchantmentId) || ctx.read.entries(row, 'enchantment_templates').length || ctx.read.entries(row, 'enchantment_template_enchantments').length)
      handBack('check_push_and_pull_after_hit:equipped-defense-carrier');
    const itemId = upper(field(row, 'inventory_item_id'));
    const definition = record(row.item_layer_types) ? row : ctx.read.definition(itemId);
    if (!definition) definitionNotHeld('check_push_and_pull_after_hit:defense-carrier-not-held', itemId);
    const keys = layerKeysDeep(ctx, definition!, new Set([itemId]));
    if (!keys) handBack('check_push_and_pull_after_hit:equipped-defense-carrier');
    if (keys.some(key => /^immune_to_forced_movement/.test(key)))
      handBack('check_push_and_pull_after_hit:carrier-defense');
  }
}
function isolated(ctx: JanusCtx): void {
  const roster = ctx.encounter.encounter_creatures_arr;
  const inventories = ctx.encounter.encounter_inventories_arr;
  if (!Array.isArray(roster) || !Array.isArray(inventories)) handBack('check_push_and_pull_after_hit:roster-not-held');
  for (const raw of [ctx.encounter, ...roster]) {
    const row = record(raw);
    if (!row) handBack('check_push_and_pull_after_hit:roster-not-held');
    // Janus session 3 step 3: the cast's EFFECTIVE statuses (rows this cast added / removed included — PHP applies them before the move).
    for (const status of activeStatuses(ctx, row)) {
      const definition = statusDefinition(ctx, status);
      if (!definition) definitionNotHeld('check_push_and_pull_after_hit:status-definition-not-held', status.status_item_id);
      // MV4: transport links hand back and a grapple drags in the twin (forced-movement.ts) — no blanket link refusal here.
      if (status.status_aoe_type != null || ctx.read.has(definition, 'persistent_aoe'))
        handBack('check_push_and_pull_after_hit:persistent-zone');
    }
  }
  // Janus session 3 step 3 (2026-09-29): a status this cast added / removed before the move no longer hands back blindly — the twin reads
  // the EFFECTIVE statuses (activeStatuses), so a condition (Blinded, Prone, Stunned, Fortunate, the cast's own status …) is simply part
  // of the state it judges. What the page still cannot reproduce hands back (statusChangeRisk).
  const risk = statusChangeRisk(ctx);
  if (risk) handBack('check_push_and_pull_after_hit:status-changed-before-move:' + risk);
}
const WALKING = 'DF516479-F164-87FA-7BB8-6F268BF1DCE7';
const LINK_IDS = new Set(['FF8D219A-2A83-0EE5-A222-E748753E8D0B', 'F13F6A58-FE91-F24D-12BE-BE7CCAD81A40', 'A1F1FAF2-DA1E-D537-36C2-2B74DC82CB12',
  'DDC66C31-4093-84AF-18BB-093D924B8738', 'E4A34093-AAAB-6453-BCE0-FBA65EE263AD', '0C2A8037-B0BC-8300-C818-198881B00E7D']);
const NESTED_LAYERS = /^(passive_ability|status_effect_applied_to_.*|.*_ability|ability|shapeshift.*)$/;
/** Every layer key in a definition's carrier tree (nested passives / applied statuses / abilities PHP's compile folds), walked to ANY depth
 *  with a PATH-local cycle guard (Codex step-3 review F1: a global "seen" set hid a node first reached deep and then granted directly) and a
 *  node budget; null = a nested definition the page does not hold, an entry it cannot identify (Codex F2: `{ability_id, item_arr}` wrappers
 *  resolve through their embedded definition first), or a tree past the budget. A discovery walk can collect missing ids instead. */
export function layerKeysDeep(ctx: { read: Pick<JanusCtx['read'], 'entries' | 'definition'> }, def: Row,
  path: ReadonlySet<string> = new Set(), budget: { nodes: number; missing?: Set<string> } = { nodes: 0 }): string[] | null {
  if (++budget.nodes > 256) return null;
  const keys = Object.keys(record(def.item_layer_types) ?? {});
  const out = [...keys];
  for (const key of keys) {
    if (!NESTED_LAYERS.test(key)) continue;
    for (const entry of ctx.read.entries(def, key)) {
      const wrapper = record(entry);
      const embedded = record(wrapper?.item_arr) ?? record(wrapper?.status_arr);
      const raw = typeof entry === 'string' ? entry.split('|')[0]
        : itemField(wrapper, 'item_id') ?? wrapper?.ability_id ?? wrapper?.status_item_id ?? itemField(embedded, 'item_id');
      const id = upper(raw);
      const known = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/.test(id);
      if (!known && !embedded && !wrapper) continue; // scalar flags are not definition references
      if (!known && !embedded) return null;
      if (known && path.has(id)) continue; // a cycle on THIS path only
      const nested = embedded && record(embedded.item_layer_types) ? embedded : known ? ctx.read.definition(id) : null;
      if (!nested) {
        if (known && budget.missing) { budget.missing.add(id); continue; }
        return null;
      }
      const deeper = layerKeysDeep(ctx, nested, known ? new Set([...path, id]) : path, budget);
      if (deeper === null) return null;
      out.push(...deeper);
    }
  }
  return out;
}
/** What a status added / removed earlier in this cast could change that the twin does not re-derive mid-cast (PHP's compile + the speed
 *  rollup refresh after apply — sync_creature_movement_speed_rollups_after_status_apply): null when nothing. Straggler run 2026-09-29
 *  (reverted narrowing) + its Codex findings: the speed refresh, the size-gated defence and canonical ids — the effective status list
 *  answers the ids, the rest are the named reasons below. */
function statusChangeRisk(ctx: JanusCtx): string | null {
  const changes = [
    ...ctx.statusesAdded.map(s => ({ id: upper(s.status_item_id), target: upper(s.target), def: statusDefinition(ctx, s as unknown as Row) })),
    ...ctx.statusesRemoved.map(s => ({ id: upper(s.status_item_id), target: upper(s.target), def: ctx.read.definition(upper(s.status_item_id)) })),
  ];
  for (const change of changes) {
    if (!change.def) return 'definition-not-held';
    if (LINK_IDS.has(change.id)) return 'link';
    const keys = layerKeysDeep(ctx, change.def, new Set([change.id]));
    if (keys === null) return 'nested-definition-not-held';
    // a forced-movement defence carried BELOW the status row itself (a passive it grants) is folded by the compile, not the status fallback
    if (keys.some(k => /^immune_to_forced_movement/.test(k))) return 'defense';
    // the body's size tier (the swept square, the landing footprint, the fall grace)
    if (keys.some(k => /size/i.test(k))) return 'size';
    if (keys.some(k => /^(persistent_aoe|aoe_follows_target|status_aoe)/.test(k))) return 'zone';
    // PHP refreshes the speed rollups after the apply; the fall twin reads speeds only for a flying / hovering / climbing mover.
    if (keys.some(k => /^(speed_|movement_)|shapeshift/.test(k))) {
      const member = ctx.members.get(change.target);
      if (!member) return 'speed-non-member';
      const preferred = upper(scalar(record(member.encounter_creatures)?.preferred_speed_type));
      if (preferred !== '' && preferred !== WALKING) return 'speed-non-walker';
    }
  }
  return null;
}
function distance(raw: unknown): number {
  const value = scalar(raw);
  if (value == null || value === '') return 0;
  // Expressions and substitutions retain a named refusal until the browser can evaluate them.
  if (typeof value !== 'number' && (typeof value !== 'string' || !/^[+-]?(?:\d+\.?\d*|\.\d+)$/.test(value.trim())))
    handBack('check_push_and_pull_after_hit:distance-formula');
  const n = Number(value);
  if (!Number.isFinite(n)) handBack('check_push_and_pull_after_hit:distance-formula');
  return n;
}

/** R-108 push collision (lane A5, 2026-10-01): a push / pull ability that also deals damage. Its callers (damageRollOneTarget, attackRoll,
 *  contestingRoll) run the effects INSIDE damageTypeRoll's `between` hook — after the fragment's dice, before its application — PHP's order
 *  (damage_type_rolls, then post_effect_conditions, whose tail is calc_total), so a collision's d6s follow the damage dice. */
export function damagingForcedMove(ctx: JanusCtx, ability: Row): boolean {
  return ctx.read.has(ability, 'damage_type')
    && (ctx.read.has(ability, 'push_ft_after_hit') || ctx.read.has(ability, 'pull_ft_after_hit') || ctx.read.has(ability, 'pull_to_caster_center'));
}

/** PHP's forced-movement call returns a VFX-only result; it inserts no roll row. */
export function checkPushAndPullAfterHit(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row, target: Row): void {
  const push = distance(ctx.read.value(ability, 'push_ft_after_hit'));
  const pull = distance(ctx.read.value(ability, 'pull_ft_after_hit'));
  const centerFlag = scalar(ctx.read.value(ability, 'pull_to_caster_center'));
  const toCaster = centerFlag != null && centerFlag !== '' && centerFlag !== 0 && centerFlag !== '0' && centerFlag !== false;
  if (push <= 0 && pull <= 0 && !toCaster) return;

  // janus_forced_movement_defense_sources(): compiled presence sources or root/status fallback.
  const defenses = record(record(record(target.computed)?.creature)?.defenses);
  if ((Array.isArray(defenses?.immune_to_forced_movement) && defenses.immune_to_forced_movement.length > 0)
      || ctx.read.has(target, 'immune_to_forced_movement')) return;
  for (const status of activeStatuses(ctx, target)) {
    const def = statusDefinition(ctx, status);
    if (!def) definitionNotHeld('check_push_and_pull_after_hit:status-definition-not-held', status.status_item_id);
    if (ctx.read.has(def, 'immune_to_forced_movement')) return;
  }
  // Source-aware size defenses still need their carrier predicate ported.
  if (ctx.read.has(target, 'immune_to_forced_movement_from_size_category_number_below'))
    handBack('check_push_and_pull_after_hit:size-gated-defense');
  for (const status of activeStatuses(ctx, target)) {
    const def = statusDefinition(ctx, status);
    if (def && ctx.read.has(def, 'immune_to_forced_movement_from_size_category_number_below'))
      handBack('check_push_and_pull_after_hit:size-gated-defense');
  }
  const encounter = record(ctx.encounter.encounters);
  if (!encounter?.encounter_shape) handBack('check_push_and_pull_after_hit:legacy-axes');
  const shapeRaw = encounter.encounter_shape;
  let shape: Row | null;
  try { shape = record(typeof shapeRaw === 'string' ? JSON.parse(shapeRaw) : shapeRaw); }
  catch { shape = null; }
  if (!shape) handBack('check_push_and_pull_after_hit:shape-not-held');
  const movementRead = forcedMovementReaders(ctx);
  shape = movementRead.runtimeShape ?? shape;
  const selection = (input.castingTargets ?? '').split(',')[0]?.trim() ?? '';
  const placedRead = (ctx.read as typeof ctx.read & { placedAoe?: PlacedAoeReaders }).placedAoe;
  const reference = !toCaster && selection.includes('|') ? movementRead.position(caster).every(Number.isFinite)
    ? placedAoeSelectionSpan(placedRead ?? ctx.read, selection, caster, ability).span.center : null : undefined;
  if (inventoryId(target) && latchedCreatures(ctx.encounter, inventoryId(target), { statuses: row => activeStatuses(ctx, row) }).length) {
    isolated(ctx);
    const vector = forcedMovementVector({ encounter: ctx.encounter, shape: shape as never, caster, target, aoe: toCaster ? null : ctx.encounter,
      reference, push, pull, toCasterCenter: toCaster }, movementRead);
    if (vector.kind === 'none') return;
    if (vector.kind === 'handback') handBack(vector.reason);
    const plan = planLatchedObjectMove(ctx.encounter, shape as never, target, vector.end, { ...movementRead,
      propFootprint: row => { const body = movementRead.inventoryCollision?.(row); return body ? { ...body.obstacle, heightFt: body.height } : null; } });
    if (plan.kind !== 'move') handBack(plan.kind === 'handback' ? plan.reason : 'latched-object:team-not-held');
    if (plan.kind === 'move') {
      landInventoryRow(ctx, target, vector.end);
      for (const member of plan.members) {
        const row = ctx.members.get(member.id);
        if (!row) handBack('latched-object:member-not-held');
        landRow(ctx, row, member.after);
        settleMovedSqueeze(ctx, row, movementRead);
      }
    }
    return;
  }
  if (!ecId(target) || !ctx.members.has(ecId(target)))
    handBack('check_push_and_pull_after_hit:non-member-target');
  // OWNER RULING 2026-10-03 (lethal push collisions — lane A11) + WORKER PICK (owner to review): a creature this use already KILLED (a push
  // collision earlier in the cast) is not moved by the rest of it either — its body stays where it died (the death tail mints the corpse
  // there); a body that was dead BEFORE the use still hands back
  if (ctx.composedDeaths?.includes(ecId(target))) return;
  if (Number(scalar(record(target.encounter_creatures)?.encounter_creature_dead) ?? 0) !== 0)
    handBack('check_push_and_pull_after_hit:dead-body-placement');
  isolated(ctx);
  // MV4 owns the displacement, hard-stop sweep, landing, grapple drag and fall prediction. Collision damage is applied below;
  // unserved falls and persistent-zone reconciliation retain their named refusals. The defence gates above stay Lane A's.
  checkDefenseCarriers(ctx, target);
  const plan = resolveForcedMovement({ encounter: ctx.encounter, shape: shape as never, caster, target, aoe: toCaster ? null : ctx.encounter,
    reference, push, pull, toCasterCenter: toCaster }, movementRead);
  if (plan.kind === 'none') return;
  if (plan.kind === 'handback') {
    // The dry runs prove displacement before collision dice. Unknown bodies or opening overlays refuse only this movement rider.
    if (/^forced-move:(?:body|other-body|position|reference-position|other-position|drag-position|inventory-shove-destination:overlay|destination-resolver:(?:body|other-body|other-position|corpse-size|overlay)|drag-destination:(?:body|other-body|other-position|corpse-size|overlay))$/.test(plan.reason)) {
      const name = String(scalar(ecLean(target, 'encounter_creature_name') ?? itemField(target, 'item_name')) ?? 'Creature');
      targetGateWarning(ctx, `Forced movement for ${name} is not supported in the browser: ${plan.reason}`, { includeTriggers: true });
      return;
    }
    handBack('check_push_and_pull_after_hit:' + plan.reason);
  }
  if (plan.zoneReconcile) handBack('check_push_and_pull_after_hit:persistent-zone');
  // COLLISION DAMAGE (resolve_push_collision_with_inventory.php, BEFORE move_target — Session A2 R-108 bucket 2): its rows precede the move
  if (plan.collision && plan.collision.dice > 0) {
    // damageTypeRoll's `between` hook prepares the spell's fragment before these collision dice, preserving the legacy draw order.
    if (ctx.read.has(ability, 'damage_type') && ctx.damagePreparedTarget !== ecId(target)) handBack('check_push_and_pull_after_hit:collision-draws-before-damage');
    (ctx.pushCollisionTouched ??= new Set()).add(ecId(target)).add(upper(plan.collision.id));
    resolvePushCollisionDamage(ctx, input, caster, target, plan.collision.kind, plan.collision.id, plan.collision.dice);
    // OWNER RULING 2026-10-03 (lethal push collisions — lane A11): a pushed creature its collision KILLED is served — its death tail names the
    // pushed creature itself as the killer (PHP's nested Take Collision Damage cast is its own; resolvePushCollisionDamage marks the tail), it
    // takes no further damage from this cast (damage-type-roll.ts), and only ONE kill is logged. WORKER PICK (owner to review): the corpse
    // lands where the push stopped it (the collision point — PHP minted the corpse before move_target and then moved the dead row).
  }
  if (plan.inventoryMove) {
    const move = plan.inventoryMove;
    const row = (ctx.encounter.encounter_inventories_arr as Row[]).find(inv => inventoryId(inv) === move.id);
    // Destruction has already removed the struck object; its rubble/spill frame replaces it.
    if (row) {
      landInventoryRow(ctx, row, move.end);
    }
  }
  const land = (row: Row, at: { x: number; y: number; z: number }, fall: FallVerdict): void => {
    if (fall.kind === 'handback') handBack('check_push_and_pull_after_hit:' + fall.reason);
    landRow(ctx, row, fall.kind === 'fall' ? { ...at, z: fall.landingZ }
      : fall.snapZ !== undefined ? { ...at, z: fall.snapZ } : at);
    if (fall.kind === 'fall' && !ctx.composedDeaths?.includes(ecId(row))) {
      const definition = ctx.read.definition(FALLING_DAMAGE_ID);
      if (!definition) definitionNotHeld('check_push_and_pull_after_hit:fall-definition-not-held', FALLING_DAMAGE_ID);
      resolveFallDamage(ctx, input, definition, row, fall.fallFt);
      // The enclosing prepared hit must re-read vitality spent by the nested fall.
      (ctx.pushCollisionTouched ??= new Set()).add(ecId(row));
    }
    if (!ctx.composedDeaths?.includes(ecId(row))) settleMovedSqueeze(ctx, row, movementRead);
  };
  // A collision can destroy a support. Re-read the working floor before any fall, including on dry runs.
  land(target, plan.end, predictFall({ row: target, at: plan.end, encounter: ctx.encounter, shape: shape as never }, movementRead));
  for (const d of plan.dragged) {
    const member = ctx.members.get(upper(d.id));
    if (!member) handBack('check_push_and_pull_after_hit:dragged-non-member');
    land(member!, d.end, predictFall({ row: member!, at: d.end, encounter: ctx.encounter, shape: shape as never, grappler: target }, movementRead));
  }
}

function settleMovedSqueeze(ctx: JanusCtx, row: Row, read: ForcedMoveReaders): void {
  const [x, y, z] = read.position(row);
  const state = squeezeStateAt(ctx.encounter, row, { ...read, geometry: member => {
    const body = read.geometry(member);
    return body && !read.meshBody?.(member) ? { diameter: body.rulesDiameter ?? body.diameter, height: body.height } : body;
  }, crossingWorld: () => ({ shape: read.runtimeShape ?? encounterShapeOf(ctx) as never,
    canvasExterior: Number(encounterField(ctx.encounter, 'encounter_inside') ?? 0) !== 1, overlayUnknown: false }) }, x, y, z);
  if (!state) handBack('check_push_and_pull_after_hit:squeeze-geometry');
  resolveMovementSqueeze20(ctx, ecId(row), state);
}

/** Persist the same explicit inventory movement shape for direct and collision-induced knockback. */
function landInventoryRow(ctx: JanusCtx, row: Row, at: { x: number; y: number; z: number }): void {
  const id = inventoryId(row), change = ctx.inventoryChanged.slice().reverse().find(entry => entry.inventory_id === id);
  if (change?.destruction) return;
  const movement = { x_pos: at.x, y_pos: at.y, z_pos: at.z };
  const held = (ctx.encounter.encounter_inventories_arr as Row[]).find(row => inventoryId(row) === id) ?? row;
  held.position = { ...record(held.position), ...movement };
  // Keep the inventory bucket's own coordinates in step (MAIN 2026-10-14; LATCH-1 review P2: dropped in the refactor).
  const bucket = record(held.inventory);
  if (bucket) Object.assign(bucket, movement);
  if (change) change.movement = movement;
  else ctx.inventoryChanged.push({ inventory_id: id, changes: {}, movement });
}

/** Moves a member row the verdict writes (its ec bucket → the commit's forcedMovements, its position, the roster's listing). */
export function landRow(ctx: JanusCtx, row: Row, at: { x: number; y: number; z: number }): void {
  const bucket = ec(row);
  // only the axes that change are written (the commit's forcedMovements and PHP's stored delta list changed columns only)
  const was = { x: posCoord(row, 'x_pos') ?? 0, y: posCoord(row, 'y_pos') ?? 0, z: posCoord(row, 'z_pos') ?? 0 };
  if (Math.abs(at.x - was.x) > 1e-9) bucket.x_pos = at.x;
  if (Math.abs(at.y - was.y) > 1e-9) bucket.y_pos = at.y;
  if (Math.abs(at.z - was.z) > 1e-9) bucket.z_pos = at.z;
  const position = record(row.position) ?? {};
  position.x_pos = at.x; position.y_pos = at.y; position.z_pos = at.z;
  row.position = position;
  for (const removal of brokenLatchRemovals(ctx.encounter, row, at, ctx.read)) {
    if (!ctx.statusesRemoved.some(saved => saved.status_id === removal.status_id)) ctx.statusesRemoved.push(removal);
  }
  row.x_pos = [at.x]; row.y_pos = [at.y]; row.z_pos = [at.z];
  const roster = ctx.encounter.encounter_creatures_arr as Row[];
  const listed = roster.find(r => ecId(r) === ecId(row));
  if (listed !== undefined && listed !== row) {
    const listedPosition = record(listed.position) ?? {};
    listedPosition.x_pos = at.x; listedPosition.y_pos = at.y; listedPosition.z_pos = at.z;
    listed.position = listedPosition;
  }
}
/** The encounter's authored FT3D shape (encounter_shape JSON), or null (a legacy XZ encounter / not held). */
export function encounterShapeOf(ctx: JanusCtx): Row | null {
  const raw = record(ctx.encounter.encounters)?.encounter_shape;
  if (!raw) return null;
  try { return record(typeof raw === 'string' ? JSON.parse(raw) : raw); } catch { return null; }
}

const STATUS_IDS: Record<string, string> = {
  riding: 'FF8D219A-2A83-0EE5-A222-E748753E8D0B', ridden: 'F13F6A58-FE91-F24D-12BE-BE7CCAD81A40', piloting: 'A1F1FAF2-DA1E-D537-36C2-2B74DC82CB12',
  piloted: 'DDC66C31-4093-84AF-18BB-093D924B8738', grappling: 'E4A34093-AAAB-6453-BCE0-FBA65EE263AD', grappled: '0C2A8037-B0BC-8300-C818-198881B00E7D',
  unconscious: 'F73BA661-ADD2-26E0-E265-FBEB7E5E6AF1', paralyzed: 'BF31EE0A-5B3C-370B-0118-96B067CFD929',
  // session 3 step 2 (Codex F2): the destination resolver's hidden-body guard reads it
  hidden: '47667406-1B72-9D60-310B-D8CF6BFE397D',
};
/** Banked physical mesh bodies use the shared collision envelope; standees use their size tier. Unheld geometry stays unknown. */
export function forcedMovementReaders(ctx: JanusCtx): ForcedMoveReaders {
  const read = ctx.read;
  const inventoryRead = (row: Row) => ({ value: read.value, definition: (): Row => {
    const definition = inventoryDefinition(row, ctx.encounter.catalog, read.definition);
    if (!definition) definitionNotHeld('check_push_and_pull_after_hit:prop-template-not-held', record(row.inventory)?.inventory_item_id);
    return definition;
  } });
  const invField = (row: Row, key: string): unknown => read.inventoryField ? read.inventoryField(row, key) : readInventoryField(row, key);
  const runtimeShape = pageSceneShape(ctx.encounter);
  const shape = runtimeShape ?? encounterShapeOf(ctx);
  const curtains = boundCurtainIds(shape);
  const doors = boundDoorIds(shape);
  const stamped = ctx.encounter.difficult_terrain_inventory_ids;
  const difficultIds = new Set((Array.isArray(stamped) ? stamped.map(upper)
    : encounterDifficultFootprints(ctx.encounter).map(row => upper(record(row)?.inventory_id))).filter(Boolean));
  const inventoryCollision: NonNullable<ForcedMoveReaders['inventoryCollision']> = (row, mover) => {
    const template = inventoryDefinition(row, ctx.encounter.catalog, read.definition);
    if (!template) definitionNotHeld('check_push_and_pull_after_hit:prop-template-not-held', invField(row, 'inventory_item_id'));
    const override = readItemUuidSlot(invField(row, 'inventory_size')).toUpperCase();
    const sizeId = inventoryAuthoredSizeId(row, template);
    const diameter = (sizeId ? SIZE_WIDTHS_FT[sizeId] : null) ?? 5;
    const capacityBase = diameter;
    const itemType = String(itemField(row, 'item_type') ?? itemField(template, 'item_type') ?? '');
    const wallRun = !!normalizeInventoryGeneration(invField(row, 'inventory_generation'))?.wallRun;
    const corpseLinked = !!invField(row, 'inventory_encounter_creature_id');
    const catalog = inventoryCatalog(row, ctx.encounter.catalog, template);
    try {
      const mesh = dedicatedPropSpace(itemType) ? null : selectedPropMeshSpaceOrTier(row, template, catalog);
      const [x, y, z] = heldMovementPosition(row);
      if (![x, y, z].every(Number.isFinite)) return null;
      const inputs = { itemType, x: x!, y: y!, z: z!, transform: normalizeTransform(invField(row, 'transform_3d')),
        sizeId, inventorySize: override || null, templateSizeId: readItemUuidSlot(read.value(template, 'size')).toUpperCase() || null,
        worldMeshBoundsM: mesh?.boundsM ?? null, worldMeshAuthoredSizeTypeId: mesh?.authoredSizeTypeId,
        shape };
      if (inputs.transform?.scaleXYZ && !mesh) handBack('check_push_and_pull_after_hit:inventory-nonuniform-structural');
      const zone = inventoryFootprint(inputs, id => SIZE_WIDTHS_FT[id] ?? null);
      const airborne = !!mover && [SPEED.flying, SPEED.hovering].some(speed => speed === upper(ecLean(mover, 'preferred_speed_type')));
      const standingZone = inventoryStandingZone({ id: inventoryId(row), itemId: upper(invField(row, 'inventory_item_id')), itemType,
        solid: true, zone, z: z!, tierFt: diameter, scale: inputs.transform?.scale ?? 1, terrainTyped: false }) ?? (airborne ? zone : null);
      const solid = inventoryOccupancyIncluded({ difficultIds, boundCurtainIds: curtains, boundDoorIds: doors }, {
        id: inventoryId(row), itemId: upper(invField(row, 'inventory_item_id')), itemType,
        solid: inventorySolid({ sceneryShell: inventorySceneryShell(row), private: Number(invField(row, 'inventory_private') ?? 0) !== 0,
          meshDrawn: inventoryMeshDrawn(row, template, catalog),
          ...inventoryFixtureFlags(key => inventoryTemplateValue(row, key, inventoryRead(row))),
          itemType, name: inventoryPropName(row, template), equipmentSlot: inventoryEquipmentSlot(row, template),
          quantity: Number(invField(row, 'item_qty') ?? 1), encounterLink: inventoryEncounterLink(row),
          wallRun, corpseLinked }) });
      if (solid === null) return null;
      const steppedOver = !airborne && !!mover && !!ecId(mover) && inventoryStepOver(readItemUuidSlot(read.value(mover, 'size')),
        inventoryStepOverSizeId(sizeId, physicalMeshBoundsFt(inputs, id => SIZE_WIDTHS_FT[id] ?? null)));
      return { diameter, height: inventoryHeightFt(inputs, id => SIZE_WIDTHS_FT[id] ?? null),
        capacity: activeStatuses(ctx, row).some(s => upper(s.status_item_id ?? itemField(record(s.status_arr), 'item_id')) === STATUS_IDS.piloted)
          ? Infinity : Math.max(0, capacityBase - 5) * 2,
        // Membership exclusions never enter the prop sweep; walls/doors use their structural geometry.
        solid: solid && standingZone !== null && !steppedOver, steppedOver, loot: !solid,
        obstacle: standingZone ?? zone };
    } catch (error) { if (error instanceof HandBack) throw error; return null; }
  };
  const body = (row: Row): { diameter: number; height: number; rulesDiameter: number; mesh: boolean } | null => {
    if (inventoryId(row) && !ecId(row)) {
      const held = inventoryCollision(row);
      if (!held) return null;
      const obstacle = held.obstacle;
      const diameter = obstacle.kind === 'oblong' ? 2 * Math.max(obstacle.halfW, obstacle.halfD) : 2 * obstacle.r;
      return { diameter, height: held.height, rulesDiameter: held.diameter, mesh: obstacle.kind === 'oblong' };
    }
    let meshes: unknown[];
    try { meshes = meshesOf(row as never); } catch { return null; }
    const physical = physicalCreatureBody(meshes);
    if (meshes.length && !physical) return null;
    if (!physical && (!sizeIdOf(row) || read.has(row, 'swarm') || read.has(row, 'swarm_size'))) return null;
    const width = sizeWidthFt(row);
    const rulesDiameter = Number.isFinite(width) && width > 0 ? width : physical?.diameter;
    return rulesDiameter ? { ...(physical ?? { diameter: rulesDiameter, height: rulesDiameter }), rulesDiameter, mesh: physical !== null } : null;
  };
  const statusId = (s: Row): string => upper(s.status_item_id ?? itemField(record(s.status_arr), 'item_id'));
  return {
    runtimeShape,
    value: (row, key) => read.value(row, key),
    entries: (row, key) => read.entries(row, key),
    definition: id => read.definition(id),
    // session 3 step 3: the cast's effective statuses (added / removed rows included)
    statuses: row => activeStatuses(ctx, row),
    hasStatus: (row, key) => !!STATUS_IDS[key] && activeStatuses(ctx, row).some(s => statusId(s) === STATUS_IDS[key]),
    geometry: body,
    // A held lean container elides zero axes; a missing container or malformed supplied axis is unknown.
    position: heldMovementPosition,
    baseSizeFt: row => body(row)?.diameter ?? null,
    heightFt: row => body(row)?.height ?? null,
    meshBody: row => body(row)?.mesh ?? false,
    opposingTeams: (left, right) => checkOpposingTeam(ctx, left, right),
    // The drag's defence re-read: the same carrier walk this module runs for the target (checkDefenseCarriers) — any carrier / equipped
    // source or an unheld definition is unknown (null); a compiled slot or a root / status layer is immune.
    immuneToForcedMovement: row => {
      const defenses = record(record(record(row.computed)?.creature)?.defenses);
      if ((Array.isArray(defenses?.immune_to_forced_movement) && defenses.immune_to_forced_movement.length > 0) || read.has(row, 'immune_to_forced_movement')) return true;
      for (const status of activeStatuses(ctx, row)) {
        const def = statusDefinition(ctx, status);
        if (!def) return null;
        if (read.has(def, 'immune_to_forced_movement')) return true;
      }
      try { checkDefenseCarriers(ctx, row); } catch { return null; }
      return false;
    },
    inventoryCollision,
    inventoryPassThrough: row => {
      if (!curtains) return null;
      const definition = inventoryRead(row).definition();
      return isTransitionNavigationProp(row, definition)
        || inventoryEncounterLink(row) || curtains.has(inventoryId(row));
    },
    inventoryExtentFt: row => {
      const body = inventoryCollision(row);
      if (!body) return null;
      const zone = body.obstacle;
      return zone.kind === 'oblong' ? Math.hypot(zone.halfW, zone.halfD) : body.diameter / 2;
    },
  };
}

/** The "Take Collision Damage" free ability (resolve_push_collision_with_inventory.php COLLISION_DAMAGE_ABILITY_ID): 1d6 bludgeoning. */
export const COLLISION_DAMAGE_ID = 'B427DACE-045F-E982-6E2A-0C4007EFCF54';
/**
 * resolve_push_collision_with_inventory.php's damage half for a CREATURE blocker (Session A2 R-108 bucket 2): when the push absorbed ≥ 5 ft,
 * `dice` = max(1, floor(absorbed / 10)) —
 *   1. apply_collision_damage_to_creature(): the PUSHED creature takes Take Collision Damage with that many d6 through a nested janus()
 *      (caster = target = itself, its own status connection) — resistances, damage reactions and death run as for any damage;
 *   2. apply_half_collision_damage_to_struck(): the STRUCK creature takes floor(roll_dice(dice, 6) / 2), drawn fresh, applied RAW through
 *      apply_damage_to_target (no resistance, no damage row) and attributed to the push's caster.
 * Both run BEFORE move_target(), so their rows precede the move.
 */
function resolvePushCollisionDamage(ctx: JanusCtx, input: JanusInput, caster: Row, target: Row, kind: 'creature' | 'inventory', struckId: string, dice: number): void {
  const struck = kind === 'creature' ? ctx.members.get(upper(struckId))
    : (ctx.encounter.encounter_inventories_arr as Row[]).find(row => inventoryId(row) === upper(struckId));
  if (!struck) handBack('check_push_and_pull_after_hit:collision-struck-not-held');
  // R-108 (lane A6, OWNER RULING 2026-10-01 "keep both damages"): a collision that strikes ANOTHER target of the same cast is served — the
  // struck half lands on the one live row and that target's own damage from the cast applies on top (PHP's write order lost one of the two:
  // census front-end rule `collision-struck-cast-target`). Was a hand-back (lane A5).
  const collision = ctx.read.definition(COLLISION_DAMAGE_ID);
  if (!collision) definitionNotHeld('check_push_and_pull_after_hit:collision-definition-not-held', COLLISION_DAMAGE_ID);
  const damage = { ...collision, item_layer_types: { ...(record(collision.item_layer_types) ?? {}), dice, die: 6 } };
  const pushedId = ecId(target);
  const connection = ctx.statusConnectedId;
  ctx.statusConnectedId = null; // apply_collision_damage_to_creature: status_connected_id new_guid()
  try {
    janus(ctx, { ...input, casterId: pushedId, targetIds: [pushedId], abilityId: COLLISION_DAMAGE_ID, castingTargets: pushedId,
      rawDiceMod: 0, rawDiceAdvDisStacks: 0, useAbilityVariable1: null, useAbilityVariable2: null }, damage, target, [target]);
  } finally { ctx.statusConnectedId = connection; }
  let rolled = 0;
  for (let i = 0; i < dice; i++) rolled += ctx.roll.d(6); // roll_dice(): one ft_rng_die per die
  const half = Math.floor(rolled / 2);
  // every collision reaches the raw path, a zero half included: it records the struck creature before its zero-total return (Round 24 P2)
  if (kind === 'inventory') applyRawDamageToInventory(ctx, caster, struck!, half);
  else applyRawDamageToCreature(ctx, input, caster, struck!, half);
}
